import type { PreviewArtifactView } from "@rakazo/core";
import { useCallback, useEffect, useRef, useState } from "react";
import type { ArtifactTarget } from "./artifact-open";
import { fetchArtifactBytes } from "./artifact-open";
import { isPreviewUrl } from "./preview-panel";

/** Fetch a fresh sealed URL this long before the current one expires. */
export const PREVIEW_REFRESH_LEAD_MS = 60_000;
/** Never refresh more often than this, even if the server hands out very short URLs. */
const PREVIEW_REFRESH_MIN_MS = 15_000;

/**
 * Reads what artifacts.get returns for a preview artifact (PIV-32, interface 1).
 * Anything that does not match the contract counts as damaged; a url outside
 * our proxy route is dropped so it can never be framed.
 */
export function parsePreviewArtifactView(bytes: Uint8Array): PreviewArtifactView | null {
  let value: unknown;
  try {
    value = JSON.parse(new TextDecoder("utf-8", { fatal: true }).decode(bytes));
  } catch {
    return null;
  }
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const { title, port, path, botId, computerId, description, url, expiresAt } = record;
  if (typeof title !== "string" || title.length === 0) return null;
  if (typeof port !== "number" || typeof path !== "string") return null;
  if (typeof botId !== "string" || typeof computerId !== "string") return null;
  if (description !== null && description !== undefined && typeof description !== "string")
    return null;
  const sealed =
    typeof url === "string" &&
    isPreviewUrl(url) &&
    typeof expiresAt === "string" &&
    !Number.isNaN(Date.parse(expiresAt));
  return {
    title,
    port,
    path,
    botId,
    computerId,
    description: typeof description === "string" ? description : null,
    url: sealed ? url : null,
    expiresAt: sealed ? expiresAt : null,
  };
}

/** Reads a preview artifact once; every read mints a fresh sealed URL. */
export async function fetchPreviewArtifact(
  target: ArtifactTarget,
  artifactId: string,
): Promise<PreviewArtifactView | null> {
  return parsePreviewArtifactView(await fetchArtifactBytes(target, artifactId));
}

/**
 * Delay until the next refresh, PREVIEW_REFRESH_LEAD_MS before expiry, but never
 * sooner than PREVIEW_REFRESH_MIN_MS after the last read. A URL that went stale
 * while nobody kept it fresh is read again right away.
 */
export function previewRefreshDelay(
  expiresAt: string,
  lastReadAt: number,
  now = Date.now(),
): number {
  const due = Date.parse(expiresAt) - PREVIEW_REFRESH_LEAD_MS;
  return Math.max(0, lastReadAt + PREVIEW_REFRESH_MIN_MS - now, due - now);
}

/** Loading state of a preview artifact and, when ready, its view. */
export type SealedPreviewState =
  | { status: "idle" | "loading" | "failed"; view: null; artifactId: string | null }
  | { status: "ready"; view: PreviewArtifactView; artifactId: string };

/**
 * Keeps a preview artifact's sealed URL fresh: read on mount, again shortly
 * before expiresAt, and whenever refresh() is called (before HEAD, on 410,
 * on reload). The stored artifact carries no token; only these reads mint one.
 * Without a target it stays idle, so callers can use it unconditionally.
 * keepFresh false stops the timer (a card scrolled out of view); turning it
 * back on reads again at once if the URL went stale meanwhile.
 */
export function useSealedPreview(
  target: ArtifactTarget | null,
  artifactId: string | null,
  keepFresh = true,
) {
  const [state, setState] = useState<SealedPreviewState>({
    status: "idle",
    view: null,
    artifactId: null,
  });
  const generation = useRef(0);
  const lastReadAt = useRef(0);
  const targetKey = target ? JSON.stringify(target) : null;

  const refresh = useCallback(async (): Promise<PreviewArtifactView | null> => {
    if (!target || !artifactId) return null;
    const mine = ++generation.current;
    lastReadAt.current = Date.now();
    try {
      const view = await fetchPreviewArtifact(target, artifactId);
      if (mine !== generation.current) return view;
      setState(
        view ? { status: "ready", view, artifactId } : { status: "failed", view: null, artifactId },
      );
      return view;
    } catch {
      if (mine === generation.current) setState({ status: "failed", view: null, artifactId });
      return null;
    }
  }, [targetKey, artifactId]);

  useEffect(() => {
    if (!artifactId || !targetKey) {
      setState({ status: "idle", view: null, artifactId: null });
      return;
    }
    setState({ status: "loading", view: null, artifactId });
    void refresh();
    return () => {
      generation.current += 1;
    };
  }, [refresh, artifactId, targetKey]);

  const expiresAt = state.view?.expiresAt ?? null;
  useEffect(() => {
    if (!expiresAt || !keepFresh) return;
    const delay = previewRefreshDelay(expiresAt, lastReadAt.current);
    const timer = window.setTimeout(() => void refresh(), delay);
    return () => window.clearTimeout(timer);
  }, [expiresAt, keepFresh, refresh]);

  // A state left over from the previous artifact never shows for the next one.
  const current: SealedPreviewState =
    state.artifactId === artifactId ? state : { status: "loading", view: null, artifactId };
  return { ...current, refresh };
}
